// services/api.js - PERBAIKAN TOTAL
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store untuk cancel tokens
const cancelTokens = new Map();

// Request interceptor - DENGAN DEBUG
api.interceptors.request.use(
  (config) => {
    // Ambil token dari localStorage
    const token = localStorage.getItem('token');
    
    // DEBUG: Log token status
    console.log(`🔑 [API Request] ${config.method?.toUpperCase()} ${config.url}`);
    console.log(`🔑 [API Request] Token from localStorage: ${token ? `PRESENT (${token.substring(0, 20)}...)` : 'MISSING ❌'}`);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`🔑 [API Request] Authorization header set: Bearer ${token.substring(0, 20)}...`);
    } else {
      console.warn(`⚠️ [API Request] No token found for request: ${config.url}`);
    }
    
    // Cancel previous request dengan endpoint yang sama
    const cancelKey = `${config.method}_${config.url}`;
    if (cancelTokens.has(cancelKey)) {
      console.log(`🛑 [API Request] Cancelling previous request: ${cancelKey}`);
      cancelTokens.get(cancelKey)();
      cancelTokens.delete(cancelKey);
    }
    
    // Setup cancel token untuk request baru
    const cancelTokenSource = axios.CancelToken.source();
    config.cancelToken = cancelTokenSource.token;
    cancelTokens.set(cancelKey, cancelTokenSource.cancel);
    
    config.metadata = { startTime: Date.now() };
    return config;
  },
  (error) => {
    console.error('❌ [API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor - DENGAN DEBUG
api.interceptors.response.use(
  (response) => {
    // Clean up cancel token
    const cancelKey = `${response.config.method}_${response.config.url}`;
    cancelTokens.delete(cancelKey);
    
    console.log(`✅ [API Response] ${response.config.url} - Status: ${response.status}`);
    
    // Normalisasi response
    if (response.data && typeof response.data === 'object') {
      if (!response.data.hasOwnProperty('data') && !response.data.hasOwnProperty('success')) {
        response.data = {
          success: true,
          data: response.data
        };
      }
    }
    return response;
  },
  (error) => {
    if (axios.isCancel(error)) {
      console.log('🛑 [API Response] Request cancelled:', error.message);
      return Promise.reject({ cancelled: true });
    }
    
    console.error('❌ [API Response Error]:', error.response?.status, error.response?.data);
    
    if (error.response?.status === 401) {
      console.warn('⚠️ [API Response] 401 Unauthorized - Clearing token and redirecting');
      localStorage.removeItem('token');
      localStorage.removeItem('fuel-monitor-storage');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper function untuk extract data dari response
export const extractData = (response) => {
  if (response?.data?.data !== undefined) return response.data.data;
  if (response?.data !== undefined) return response.data;
  return response;
};

// Set token secara manual (dipanggil setelah login)
export const setAuthToken = (token) => {
  if (token) {
    console.log('🔑 [setAuthToken] Setting token in localStorage and API headers');
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('🔑 [setAuthToken] Verification - Header set:', !!api.defaults.headers.common['Authorization']);
  } else {
    console.log('🔑 [setAuthToken] Removing token');
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
  }
};

// Get current token
export const getAuthToken = () => {
  const token = localStorage.getItem('token');
  console.log('🔑 [getAuthToken] Token retrieved:', token ? `PRESENT (${token.substring(0, 20)}...)` : 'MISSING');
  return token;
};

// Cancel all pending requests
export const cancelAllRequests = () => {
  console.log('🛑 [cancelAllRequests] Cancelling all pending requests');
  cancelTokens.forEach((cancel, key) => {
    cancel();
    cancelTokens.delete(key);
  });
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Company API
export const companyAPI = {
  getAll: () => api.get('/companies'),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

// User API
export const userAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Vessel API - LENGKAP
export const vesselAPI = {
  getAll: async () => {
    const response = await api.get('/vessels');
    return { data: extractData(response) };
  },
  
  getById: async (id) => {
    if (!id || id === 'ID_KAPAL' || id.length < 10) {
      throw new Error('Invalid vessel ID');
    }
    const response = await api.get(`/vessels/${id}`);
    return { data: extractData(response) };
  },
  
  create: (data) => api.post('/vessels', data),
  update: (id, data) => api.put(`/vessels/${id}`, data),
  delete: (id) => api.delete(`/vessels/${id}`),
  
  getSensors: async (id) => {
    if (!id || id.length < 10) {
      return { data: [] };
    }
    try {
      const response = await api.get(`/vessels/${id}/sensors`);
      return { data: extractData(response) || [] };
    } catch (err) {
      if (!err.cancelled) console.log('No sensors for vessel:', id);
      return { data: [] };
    }
  },
  
  updateSensors: async (id, sensors) => {
    const response = await api.put(`/vessels/${id}/sensors`, { sensors });
    return { data: extractData(response) };
  },
  
  getSensorValues: async (id) => {
    if (!id || id.length < 10) {
      return { data: {} };
    }
    try {
      const response = await api.get(`/vessels/${id}/sensors/values`);
      return { data: extractData(response) || {} };
    } catch (err) {
      if (!err.cancelled) console.log('No sensor values for vessel:', id);
      return { data: {} };
    }
  },
  
  getPosition: async (id) => {
    if (!id || id.length < 10) {
      return { data: {} };
    }
    try {
      const response = await api.get(`/vessels/${id}/position`);
      return { data: extractData(response) || {} };
    } catch (err) {
      if (!err.cancelled) console.log('No position for vessel:', id);
      return { data: {} };
    }
  },
  
  getHistory: async (id, params) => {
    if (!id || id.length < 10) {
      return { data: [] };
    }
    try {
      const response = await api.get(`/vessels/${id}/history`, { params });
      return { data: extractData(response) || [] };
    } catch (err) {
      if (!err.cancelled) console.log('No history for vessel:', id);
      return { data: [] };
    }
  },
  
  getFuelEstimation: async (id) => {
    const response = await api.get(`/vessels/${id}/fuel-estimation`);
    return { data: extractData(response) };
  },
  
  updateFuelEstimation: async (id, data) => {
    const response = await api.put(`/vessels/${id}/fuel-estimation`, data);
    return { data: extractData(response) };
  },
  
  // API Key Management
  generateApiKey: async (id) => {
    const response = await api.post(`/vessels/${id}/generate-api-key`);
    return { data: extractData(response) };
  },
  
  rotateApiKey: async (id) => {
    const response = await api.post(`/vessels/${id}/rotate-api-key`);
    return { data: extractData(response) };
  },
  
  revokeApiKey: async (id) => {
    const response = await api.delete(`/vessels/${id}/revoke-api-key`);
    return { data: extractData(response) };
  },
  
  getApiStatus: async (id) => {
    const response = await api.get(`/vessels/${id}/api-status`);
    return { data: extractData(response) };
  },
  
  // GPS History
  getGpsHistory: async (id, params) => {
    if (!id || id.length < 10) {
      return { data: [] };
    }
    try {
      const response = await api.get(`/vessels/${id}/gps-history`, { params });
      return { data: extractData(response) || [] };
    } catch (err) {
      if (!err.cancelled) console.log('No GPS history for vessel:', id);
      return { data: [] };
    }
  },
};

// Trip API
export const tripAPI = {
  getAll: async (params) => {
    const response = await api.get('/trips', { params });
    return { data: extractData(response) || [] };
  },
  getById: async (id) => {
    const response = await api.get(`/trips/${id}`);
    return { data: extractData(response) };
  },
  getPlayback: async (id) => {
    const response = await api.get(`/trips/${id}/playback`);
    return { data: extractData(response) };
  },
  getActiveTrips: async () => {
    const response = await api.get('/trips/status/active');
    return { data: extractData(response) || [] };
  },
};

// Fuel API
export const fuelAPI = {
  getConsumption: async (params) => {
    const response = await api.get('/fuel/consumption', { params });
    return { data: extractData(response) || [] };
  },
  getAnomalies: async (params) => {
    const response = await api.get('/fuel/anomalies', { params });
    return { data: extractData(response) || [] };
  },
  getVesselComparison: async (params) => {
    try {
      const response = await api.get('/fuel/vessel-comparison', { params });
      return { data: extractData(response) || [] };
    } catch (err) {
      if (!err.cancelled) console.log('No comparison data');
      return { data: [] };
    }
  },
  getDashboardStats: async (params) => {
    const response = await api.get('/fuel/dashboard-stats', { params });
    return { data: extractData(response) || {} };
  },
  getRealtimeConsumption: async (params) => {
    const response = await api.get('/fuel/consumption/realtime', { params });
    return { data: extractData(response) || {} };
  },
  getSpeedTrend: async (params) => {
    try {
      const response = await api.get('/fuel/speed-trend', { params });
      return { data: extractData(response) || [] };
    } catch (err) {
      return { data: [] };
    }
  },
};

// Reports API
export const reportsAPI = {
  getFuelReport: async (params) => {
    const response = await api.get('/reports/fuel', { params });
    return { data: extractData(response) || {} };
  },
  getTripReport: async (params) => {
    const response = await api.get('/reports/trips', { params });
    return { data: extractData(response) || {} };
  },
  getPerformanceReport: async (params) => {
    const response = await api.get('/reports/performance', { params });
    return { data: extractData(response) || {} };
  },
  exportReport: (type, params) => api.get(`/reports/export/${type}`, { params, responseType: 'blob' }),
};

export default api;